import { Grid } from "@mui/material";
import { ReactComponent as Priority } from "assets/Priority.svg";
import ThemeContext from "components/app/Context";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import { addToMyPlansAsync, updatePlanAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { SectionDetail } from "components/features/planDetails/PlanDetail";
import { isEmpty, isFunction } from "lodash";
import * as React from "react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { ABOVE_PROPERTY, messages } from "utils/CommonConstants";
import { converter_ArrayString, functionalAreaIdsToNames, getHltInncodes } from "utils/CommonFunctions";
import { getPlanDetailsRoute } from "utils/StoreUtils";
import { formTypes } from "utils/Types";
import Input from "../../../common/Input";
import RichText from "../../../common/RichText";
import Calender from "../../../common/calender/Calender";
import DropDown from "../../../common/dropdowns/DropDown";
import MultipleSelectCheckmarks from "../../../common/dropdowns/MultiSelect";

interface Props {
  actionPayload?: any;
  sectionPayload?: SectionDetail[];
  handleCloseDialog?: () => void;
  formType?: string;
  isTemplateType?: boolean;
}

function Form(props: Props, ref: any) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { isMobileScreen } = React.useContext(ThemeContext);

  const {
    actionPayload,
    sectionPayload = [],
    handleCloseDialog,
    formType = formTypes.CreatePlan,
    isTemplateType = false
  } = props;

  const innCode = useAppSelector((state: any) => state.app.innCode);
  const functionalAreaList: any = useAppSelector((state) => state.functionalAreas.list);
  const myPlanDetails: any = useAppSelector((state) => state.myPlanDetails.myPlanDetails);
  const isViewMode: any = useAppSelector((state) => state.myPlanDetails.isViewMode);
  const showField = formType === formTypes.CreatePlan || !myPlanDetails?.planType; //For deadline and initiative plan edit.

  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<any>({
    defaultValues: {
      functionalArea:
        location.pathname.split("/")[1] === "functionalArea"
          ? [location.pathname.split("/")[2]]
          : myPlanDetails?.functionalAreaIds,
      inncodes: location.pathname.split("/")[1] === "myplans" ? "" : myPlanDetails?.innCode || innCode
    }
  });

  const [planTitle, setPlanTitle] = useState("");
  const [planFunctionalAreaIds, setPlanFunctionalAreaIds] = React.useState<string[]>(
    myPlanDetails?.functionalAreaIds || []
  );
  const [planDescription, setPlanDescription] = useState("");
  const [planPriority, setPlanPriority] = useState();
  const [planInncode, setPlanInncode] = useState();

  const getDataRefreshTag = () => {
    if (
      (isTemplateType && searchParams.get("parentTab") === "Template Plans") ||
      (!isTemplateType && searchParams.get("parentTab") === "Property Plans")
    )
      return true;
    else return false;
  };

  const onSubmit = async (data: any) => {
    let payload;
    isFunction(handleCloseDialog) && handleCloseDialog();

    if (!isEmpty(actionPayload) || !isEmpty(sectionPayload)) {
      payload = {
        innCode: innCode,
        priority: planPriority,
        dueDate: data.dueDate,
        libraryPlan: {
          name: planTitle,
          summary: planDescription,
          functionalAreaIds: planFunctionalAreaIds,
          sections: sectionPayload?.length
            ? sectionPayload
            : [
                {
                  cards: actionPayload
                }
              ]
        }
      };
    } else {
      payload = {
        innCode: isTemplateType ? ABOVE_PROPERTY : planInncode ?? innCode,
        priority: planPriority,
        dueDate: data.dueDate,
        shouldDataRefresh: getDataRefreshTag(),
        libraryPlan: {
          name: planTitle,
          summary: planDescription,
          functionalAreaIds: planFunctionalAreaIds
        }
      };
    }
    const response = await dispatch(addToMyPlansAsync(payload));
    const route = getPlanDetailsRoute();
    if (!isViewMode) {
      if (response?.payload?.id) navigate(`/${route}/${response?.payload?.id}`);
      dispatch(
        setFeedbackDetails({
          shouldShow: true,
          variant: "success",
          setState: setFeedbackDetails({ shouldShow: false }),
          message: messages.planAdded
        })
      );
    }
    return response.payload;
  };

  const onSubmitEdit = async (data: any) => {
    isFunction(handleCloseDialog) && handleCloseDialog();
    let payload;

    if (myPlanDetails?.planType) {
      payload = {
        priority: planPriority ?? myPlanDetails?.priority,
        dueDate: data.dueDate
      };
    } else {
      payload = {
        priority: planPriority ?? myPlanDetails?.priority,
        dueDate: data.dueDate,
        title: planTitle || myPlanDetails?.name,
        description: planDescription || myPlanDetails?.summary,
        functionalAreas: planFunctionalAreaIds.length > 0 ? planFunctionalAreaIds : myPlanDetails?.functionalAreaIds
      };
    }

    const response = await dispatch(updatePlanAsync({ url: `${myPlanDetails?.id}`, payload }));
    if (response.payload?.id && !isViewMode) {
      dispatch(
        setFeedbackDetails({
          shouldShow: true,
          variant: "success",
          setState: setFeedbackDetails({ shouldShow: false }),
          message: messages.editSuccess
        })
      );
    }
  };

  React.useImperativeHandle(ref, () => ({
    submitForm: async () => {
      return new Promise((resolve) => {
        handleSubmit(
          async (data) => {
            const apiRes = await onSubmit(data);

            resolve({
              success: true,
              data,
              apiRes
            });
          },
          (errors) => {
            resolve({
              success: false,
              errors
            });
          }
        )();
      });
    },

    submitEditForm: async () => {
      return new Promise((resolve) => {
        handleSubmit(
          async (data) => {
            const apiRes = await onSubmitEdit(data);

            resolve({
              success: true,
              data,
              apiRes
            });
          },
          (errors) => {
            resolve({
              success: false,
              errors
            });
          }
        )();
      });
    }
  }));

  const getfunctionalAreas = (val: any) => {
    const areaDetails = val?.map((item: any) => functionalAreaList.find((FA: any) => item === FA?.name)?.id);
    setPlanFunctionalAreaIds(areaDetails);
  };

  return (
    <Grid container margin="auto">
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
        {showField && (
          <>
            <Grid item xs={12}>
              <Input
                label="Title"
                name="title"
                register={register}
                error={errors?.title}
                validations={{ required: true }}
                handleChangeUpdate={(title) => setPlanTitle(title)}
                textVariant="body2"
                placeholder="Add a brief title"
                sx={{ width: "100%" }}
                color={tokens.Text.General.PrimaryHeadline}
                value={formType === formTypes.EditPlan ? myPlanDetails?.name : ""}
              />
            </Grid>
            <Grid item xs={12} display="flex" gap={1} marginTop="16px">
              {formType === formTypes.EditPlan || isTemplateType ? (
                <></>
              ) : (
                <Grid item xs={6}>
                  <Controller
                    name="inncodes"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <DropDown
                        validations={{ required: true }}
                        label="Inncode"
                        disabled={!isEmpty(actionPayload) || !isEmpty(sectionPayload)}
                        error={errors?.inncodes}
                        options={converter_ArrayString(getHltInncodes())}
                        initialValue={field.value}
                        handleChangeUpdate={(id) => {
                          field.onChange(id);
                          setPlanInncode(id);
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={isTemplateType ? 12 : 6}>
                <Controller
                  name="functionalArea"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <MultipleSelectCheckmarks
                      validations={{ required: true }}
                      isFormInput
                      minWidth="100%"
                      label="Functional area"
                      options={functionalAreaList}
                      error={errors?.functionalArea}
                      initialValues={functionalAreaIdsToNames(field.value || [])}
                      onChange={(val: any) => {
                        field.onChange(val);
                        getfunctionalAreas(val);
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} marginTop="16px">
              <RichText
                placeholder="Write the description"
                initialValue={formType === formTypes.EditPlan ? myPlanDetails?.summary || "" : ""}
                handleChangeUpdate={(desc) => setPlanDescription(desc || null)}
                label="Description"
              />
            </Grid>
          </>
        )}
        {!isTemplateType && (
          <Grid item xs={12} display="flex" gap={1} marginTop="16px" flexWrap={isMobileScreen ? "wrap" : "nowrap"}>
            <Grid item xs={12} md={6}>
              <DropDown
                startAdornment={<Priority />}
                label="Priority"
                handleChangeUpdate={(val) => setPlanPriority(val || null)}
                options={converter_ArrayString(["Low", "Medium", "High"])}
                initialValue={formType === formTypes.EditPlan ? myPlanDetails.priority : ""}
                sx={{ margin: "4px 0px", "& .MuiSelect-select": { marginLeft: "11.43px" } }}
              />
            </Grid>
            <Grid item xs={12} md={6} marginTop="4px">
              <Controller
                name="dueDate"
                control={control}
                render={({ field: { onChange, name } }) => (
                  <Calender
                    onChange={onChange}
                    name={name}
                    error={errors?.dueDate}
                    defaultValue={formType === formTypes.EditPlan ? myPlanDetails?.dueDate : null}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </form>
    </Grid>
  );
}

export default React.forwardRef(Form);
